import React, { useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import SkuflixCrossIcon from "core/icons/skuflix/SkuflixCrossIcon";
import Typography from "@mui/material/Typography";
import { useQuery } from "@apollo/client";
import { ChatOnlineMembersQuery } from "core/queries";
import { useTranslation } from "react-i18next";
import useWsLinkStatus from "core/hooks/useWsLinkStatus";

function ChatOnlineList({
  open,
  onClose,
  chatRoomId,
  onMention,
  onlineMembers,
}) {
  const [t] = useTranslation("core");
  const { online: wsConnected } = useWsLinkStatus();
  const hasLiveMembers = onlineMembers !== undefined;

  const { data: queryData, refetch } = useQuery(ChatOnlineMembersQuery, {
    skip: !open || !chatRoomId || hasLiveMembers,
    variables: { chatRoomId },
    fetchPolicy: "cache-and-network",
  });

  // Polling fallback when WS is disconnected
  useEffect(() => {
    if (wsConnected) return;
    if (!open || !chatRoomId) return;
    if (hasLiveMembers) return;

    const interval = setInterval(() => {
      refetch();
    }, 15000);

    return () => clearInterval(interval);
  }, [wsConnected, open, chatRoomId, refetch, hasLiveMembers]);

  const members = hasLiveMembers
    ? onlineMembers
    : queryData?.chatRoom?.onlineMembers || [];

  const handleMemberClick = (member) => {
    if (onMention) {
      onMention({
        userId: member.userId,
        userDisplayName: member.userDisplayName,
      });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        <Typography component="span" variant="h6" sx={{ flex: 1 }}>
          {t("chat.onlineMembers")} ({members.length})
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <SkuflixCrossIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <List>
          {members.map((member) => (
            <ListItem
              key={member.id}
              button
              onClick={() => handleMemberClick(member)}
            >
              <ListItemAvatar>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  variant="dot"
                  color="success"
                >
                  <Avatar src={member.userAvatar} />
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={member.userDisplayName}
                secondary={
                  member.userIsAdmin
                    ? t("chat.admin")
                    : member.userIsRightholder
                    ? t("chat.rightholder")
                    : null
                }
              />
            </ListItem>
          ))}
          {members.length === 0 && (
            <ListItem>
              <ListItemText
                primary={t("chat.noOnlineMembers")}
                sx={{ textAlign: "center" }}
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
}

ChatOnlineList.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  chatRoomId: PropTypes.string,
  onMention: PropTypes.func,
  onlineMembers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      userId: PropTypes.string.isRequired,
      userDisplayName: PropTypes.string.isRequired,
      userAvatar: PropTypes.string,
      userIsRightholder: PropTypes.bool,
      userIsAdmin: PropTypes.bool,
    })
  ),
};

export default ChatOnlineList;
